import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { RefreshIndicator } from "@/components/RefreshIndicator";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SeatFilters } from "@/components/seat/SeatFilters";
import { ProductCard } from "@/components/seat/ProductCard";
import { DepartureDialog } from "@/components/seat/DepartureDialog";
import { ProductDialog } from "@/components/seat/ProductDialog";
import { ProductDetail } from "@/components/seat/ProductDetail";
import { SeatBulk } from "@/components/seat/SeatBulk";
import { STATUS, EMPTY_FILTER, applyFilters, todayISO } from "@/lib/seat";

const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((a, b) => a.localeCompare(b, "id"));

export default function SeatStatus() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user.role);
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState(EMPTY_FILTER);
  const [loading, setLoading] = useState(true);
  const [lastLoaded, setLastLoaded] = useState("");
  const [depState, setDepState] = useState({ open: false });
  const [prodState, setProdState] = useState({ open: false });
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async (manual) => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([api.get("/seat/products"), api.get("/templates")]);
      setProducts(p.data);
      setTemplates(t.data);
      setLastLoaded(new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
      if (manual === true) toast.success("Data diperbarui");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => uniq(products.map((p) => p.group)), [products]);
  const cities = useMemo(() => uniq(products.map((p) => p.departure_city)), [products]);
  const years = useMemo(() => uniq(products.flatMap((p) => p.departures.filter((d) => d.date >= todayISO()).map((d) => d.date.slice(0, 4)))), [products]);
  const list = useMemo(() => applyFilters(products, f), [products, f]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/seat/products/${toDelete.id}`);
      toast.success("Produk dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
        <div>
          <p className="overline-label">Program CS</p>
          <h1 className="font-heading mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Status Seat</h1>
          <p className="mt-2 text-sm text-slate-500">Cek status keberangkatan tiap produk, klik tanggal untuk mengubah status, lalu salin itinerary.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <RefreshIndicator loading={loading} lastLoaded={lastLoaded} onRefresh={() => load(true)} />
          {isAdmin && <SeatBulk onDone={load} />}
          {isAdmin && <Button onClick={() => setProdState({ open: true, product: null })} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid="seat-add-product-button"><Plus className="h-4 w-4 mr-1" /> Tambah Produk</Button>}
        </div>
      </div>

      <SeatFilters f={f} set={(p) => setF((x) => ({ ...x, ...p }))} groups={groups} cities={cities} years={years} />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="text-xs text-slate-500" data-testid="seat-count">{list.length} produk ditemukan</p>
        <div className="flex flex-wrap gap-3">
          {Object.entries(STATUS).map(([k, s]) => <span key={k} className="inline-flex items-center gap-1.5 text-xs text-slate-500"><span className={`h-2 w-2 rounded-full ${s.dot}`} />{s.label}</span>)}
        </div>
      </div>

      {list.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="seat-empty">
          <Inbox className="h-10 w-10 text-slate-300" />
          <p className="mt-4 font-medium text-slate-700">{products.length === 0 ? "Belum ada produk" : "Tidak ada produk yang cocok"}</p>
          <p className="mt-1 text-sm text-slate-500">Coba ubah filter atau rentang tanggal.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {list.map((p, i) => (
            <ProductCard key={p.id} p={p} index={i} isAdmin={isAdmin} onDetail={setDetail} onEdit={(x) => setProdState({ open: true, product: x })} onDelete={setToDelete}
              onAddDep={(x) => setDepState({ open: true, product: x, dep: null })} onEditDep={(d) => setDepState({ open: true, product: p, dep: d })} />
          ))}
        </div>
      )}

      <DepartureDialog state={depState} onClose={() => setDepState({ open: false })} onSaved={load} />
      <ProductDetail product={detail} templates={templates} onClose={() => setDetail(null)} />
      {isAdmin && (
        <>
          <ProductDialog state={prodState} templates={templates} onClose={() => setProdState({ open: false })} onSaved={load} />
          <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={confirmDelete}
            title="Hapus produk?" description={`Produk "${toDelete?.name}" beserta semua tanggal keberangkatannya akan dihapus permanen.`} />
        </>
      )}
    </div>
  );
}
